import { Box, Chip, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { OverallRanking } from '../../types/results';
import type { Discipline, PublicDisplayConfig } from '../../types/domain';
import { Podium, type PodiumEntry } from './Podium';
import { RankBadge } from './RankBadge';

interface OverallPodiumViewProps {
  ranking: OverallRanking;
  disciplines: Discipline[];
  config: PublicDisplayConfig;
  highlightTeamId?: number | null;
}

export const OverallPodiumView = ({ ranking, disciplines, config, highlightTeamId }: OverallPodiumViewProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sorted = [...ranking.teams].sort((a, b) => a.finalRank - b.finalRank);
  const topThree: PodiumEntry[] = sorted
    .filter((t) => t.finalRank >= 1 && t.finalRank <= 3)
    .map((t) => ({
      teamId: t.teamId,
      rank: t.finalRank as 1 | 2 | 3,
      teamName: t.teamName,
      startNumber: t.startNumber,
      primaryValue: String(t.totalPoints),
      primaryLabel: 'Celkem bodů',
    }));

  const rest = sorted.filter((t) => t.finalRank > 3);

  if (sorted.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
        <Typography variant="h6" fontWeight={600}>
          Žádná družstva v této kategorii
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {topThree.length > 0 && config.showPoints && (
        <Podium entries={topThree} showStartNumber={config.showStartNumber} />
      )}
      {topThree.length > 0 && !config.showPoints && (
        <Podium
          entries={topThree.map((e) => ({ ...e, primaryValue: `${e.rank}. místo`, primaryLabel: undefined }))}
          showStartNumber={config.showStartNumber}
        />
      )}

      {rest.length > 0 && (
        <>
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'text.secondary', mb: 1, display: 'block' }}
          >
            Další pořadí
          </Typography>
          <Stack spacing={1}>
            {rest.map((t) => {
              const isHighlight = highlightTeamId === t.teamId;
              return (
                <Paper
                  key={t.teamId}
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: 1.5,
                    borderColor: isHighlight ? 'primary.main' : 'divider',
                    bgcolor: isHighlight ? 'action.selected' : 'background.paper',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <RankBadge rank={t.finalRank} size={32} />
                    {config.showStartNumber && (
                      <Chip size="small" label={`#${t.startNumber}`} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                    )}
                    <Typography fontWeight={isHighlight ? 700 : 500} sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                      {t.teamName}
                    </Typography>
                    {!isMobile && config.showPoints && (
                      <Typography fontFamily="monospace" fontWeight={700} color="primary.main" sx={{ fontSize: '1.1rem' }}>
                        {t.totalPoints} b.
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ flexShrink: 0 }}>
                    {disciplines.map((d) => {
                      const r = t.perDisciplineRanks[d.id];
                      return (
                        <Chip
                          key={d.id}
                          size="small"
                          label={`${d.name.slice(0, 3).toUpperCase()}: ${r ?? '—'}`}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      );
                    })}
                  </Stack>
                  {isMobile && config.showPoints && (
                    <Typography fontFamily="monospace" fontWeight={700} color="primary.main" sx={{ alignSelf: 'flex-end' }}>
                      {t.totalPoints} bodů
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </>
      )}
    </Box>
  );
};
